/**
 * The task board: six columns on desktop, status tabs plus a filtered list
 * at mobile widths.
 *
 * Both presentations are the same data and the same DOM — CSS decides which
 * one shows, and the tabs only choose which column is visible. Column
 * headers carry the server's counts, which are already filtered to what
 * this caller may see; the client neither computes nor corrects them.
 *
 * Within a column, the order is deliberate and predictable: highest
 * *approved* priority first, then the oldest-created task first — the queue
 * discipline "urgent before important, and first-come within equals". A
 * Backlog suggestion never orders anything: it is a proposal, and ordering
 * by it would dress it as a decision. (The server's flat listing stays
 * newest-first within priority — a feed order; a column is a queue.)
 */

import { useCallback, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { fetchTasks, type TaskSummary } from '../api.js';
import {
  TaskCard,
  TaskStateChip,
  TaskTypeFilter,
  useWorkspaceCopy,
} from '../components.js';
import { useSession } from '../session.js';
import { Button } from '../ui.js';
import { FailurePanel, LoadingPanel, PageTitle, useAsync } from './common.js';

const BOARD_ORDER = [
  'BACKLOG',
  'READY',
  'IN_PROGRESS',
  'IN_REVIEW',
  'DONE',
  'REJECTED',
] as const;

/** Approved-priority rank; anything unranked (including null) sinks last. */
const PRIORITY_RANK: Readonly<Record<string, number>> = { P1: 1, P2: 2, P3: 3, P4: 4 };

/**
 * The column ordering rule, as one pure function: approved priority first,
 * then oldest created, then id as the deterministic tiebreak.
 */
export function orderForColumn(tasks: readonly TaskSummary[]): readonly TaskSummary[] {
  return [...tasks].sort((a, b) => {
    const rankA = a.priority === null ? 99 : (PRIORITY_RANK[a.priority] ?? 99);
    const rankB = b.priority === null ? 99 : (PRIORITY_RANK[b.priority] ?? 99);
    if (rankA !== rankB) return rankA - rankB;
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
    return Number(a.id) - Number(b.id);
  });
}

export function BoardPage(): ReactNode {
  const copy = useWorkspaceCopy();
  const navigate = useNavigate();
  const { session } = useSession();
  const [typeFilter, setTypeFilter] = useState<string | null>(null);
  const { state, reload } = useAsync(
    useCallback(
      () => fetchTasks(typeFilter === null ? {} : { types: [typeFilter] }),
      [typeFilter],
    ),
  );
  const [activeTab, setActiveTab] = useState<string>('READY');

  const visibleTypes =
    session.status === 'authenticated' ? session.profile.visibleTaskTypes : [];

  const open = useCallback(
    (taskId: string) => {
      void navigate(`/tasks/${taskId}`);
    },
    [navigate],
  );

  if (state.status === 'loading') return <LoadingPanel />;
  if (state.status === 'failed')
    return <FailurePanel failure={state.failure} onRetry={reload} />;

  const { tasks, counts } = state.value;

  return (
    <>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <PageTitle>{copy.board.title}</PageTitle>
        <div
          style={{
            display: 'flex',
            gap: 'var(--space-md)',
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <TaskTypeFilter
            visibleTypes={visibleTypes}
            value={typeFilter}
            onChange={setTypeFilter}
          />
          <Button
            variant="primary"
            onClick={() => {
              void navigate('/tasks/new');
            }}
          >
            {copy.board.propose}
          </Button>
        </div>
      </div>

      {/* Mobile: one tab per status. Hidden above 768px by the stylesheet. */}
      <div
        className="mj-status-tabs"
        role="tablist"
        aria-label={copy.board.statusTabsLabel}
      >
        {BOARD_ORDER.map((status) => (
          <button
            key={status}
            type="button"
            role="tab"
            aria-selected={activeTab === status}
            onClick={() => {
              setActiveTab(status);
            }}
          >
            {copy.statuses[status]} · {counts[status] ?? 0}
          </button>
        ))}
      </div>

      <div className="mj-board-region" aria-label={copy.board.boardLabel}>
        <div className="mj-board">
          {BOARD_ORDER.map((status) => {
            const column = orderForColumn(
              tasks.filter((task) => task.status === status),
            );
            return (
              <section
                key={status}
                className="mj-board-column"
                data-active={activeTab === status}
                aria-label={`${copy.statuses[status] ?? status} — ${copy.board.columnCount(counts[status] ?? 0)}`}
              >
                <header className="mj-board-column-header">
                  <TaskStateChip status={status} />
                  <span
                    style={{
                      font: 'var(--type-body-tabular)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {counts[status] ?? 0}
                  </span>
                </header>

                {column.length === 0 ? (
                  <p
                    style={{
                      font: 'var(--type-caption)',
                      color: 'var(--text-muted)',
                      textAlign: 'center',
                      padding: 'var(--space-md) 0',
                    }}
                  >
                    {copy.board.emptyColumn}
                  </p>
                ) : (
                  column.map((task) => (
                    // The column header already names the status; the card
                    // spends the room on execution state instead.
                    <TaskCard
                      key={task.id}
                      task={task}
                      onOpen={open}
                      showStatus={false}
                    />
                  ))
                )}
              </section>
            );
          })}
        </div>
      </div>
    </>
  );
}
